import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Slider,
  TextField,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Psychology as PsychologyIcon,
  ImageSearch as ImageSearchIcon,
  Category as CategoryIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import { getServerUrl } from '../config/runtimeConfig';
import axiosInstance from '../api/axiosInstance';
import AuthenticatedImage from './AuthenticatedImage';
import ImageAnalysisDetailsDialog from './ImageAnalysisDetailsDialog';
import QualityReportTab from './QualityReportTab';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Memoized cluster card to prevent unnecessary re-renders
const ClusterCard = React.memo(({ 
  cluster, 
  clusterIndex, 
  imageDataMap, 
  clusterThumbnailCount,
  onShowMore,
  onShowLess,
  onShowAnalysis
}) => {
  return (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip 
                label={`#${clusterIndex + 1}`} 
                size="small" 
                color="secondary" 
                sx={{ fontWeight: 'bold' }}
              />
              <Typography variant="h6">{cluster.clusterLabel || 'Unnamed Cluster'}</Typography>
            </Box>
            <Chip label={`${cluster.imageCount || 0} images`} color="primary" />
          </Box>

          {cluster.commonTags && cluster.commonTags.length > 0 && (
            <Box sx={{ mb: 1 }}>
              {cluster.commonTags.map((tag, idx) => (
                <Chip key={idx} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
              ))}
            </Box>
          )}

          {cluster.sceneType && (
            <Typography variant="body2" color="text.secondary">
              Scene: {cluster.sceneType}
            </Typography>
          )}

          {cluster.timeRange && (() => {
            const startDate = new Date(cluster.timeRange.start).toLocaleDateString();
            const endDate = new Date(cluster.timeRange.end).toLocaleDateString();
            return (
              <Typography variant="body2" color="text.secondary">
                Time: {startDate} - {endDate}
              </Typography>
            );
          })()}

          {/* Image Thumbnails */}
          {cluster.images && cluster.images.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {cluster.images.slice(0, clusterThumbnailCount).map((image) => {
                  const imageData = imageDataMap.get(image.imageId);
                  const imageUniqueId = imageData?.uniqueId || image.imageId;
                  const imageFilename = imageData?.filename || imageData?.name || 'Image';
                  
                  return imageData ? (
                    <AuthenticatedImage
                      key={image.imageId}
                      imageId={imageUniqueId}
                      type="thumbnail"
                      alt={imageFilename}
                      style={{
                        width: 80,
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 4,
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (onShowAnalysis) {
                          onShowAnalysis(imageUniqueId);
                        }
                      }}
                    />
                  ) : null;
                })}
                {cluster.images.length > clusterThumbnailCount && (
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.200',
                      borderRadius: 1,
                      color: 'text.secondary'
                    }}
                  >
                    +{cluster.images.length - clusterThumbnailCount}
                  </Box>
                )}
              </Box>
              {cluster.images.length > clusterThumbnailCount && (
                <Button size="small" onClick={onShowMore} sx={{ mt: 1 }}>
                  Show More
                </Button>
              )}
              {clusterThumbnailCount > 20 && (
                <Button size="small" onClick={onShowLess} sx={{ mt: 1, ml: 1 }}>
                  Show Less
                </Button>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No images to display
            </Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
});

// Wrapper component to create stable callback references
const ClusterCardWrapper = React.memo(({ 
  cluster, 
  clusterIndex, 
  imageDataMap, 
  expandedClusters,
  handleShowMore,
  handleShowLess,
  onShowAnalysis
}) => {
  const clusterThumbnailCount = expandedClusters[cluster.clusterId] || 20;
  
  const onShowMore = useCallback(() => {
    handleShowMore(cluster.clusterId, clusterThumbnailCount, cluster.images.length);
  }, [cluster.clusterId, clusterThumbnailCount, cluster.images.length, handleShowMore]);
  
  const onShowLess = useCallback(() => {
    handleShowLess(cluster.clusterId);
  }, [cluster.clusterId, handleShowLess]);
  
  return (
    <ClusterCard
      cluster={cluster}
      clusterIndex={clusterIndex}
      imageDataMap={imageDataMap}
      clusterThumbnailCount={clusterThumbnailCount}
      onShowMore={onShowMore}
      onShowLess={onShowLess}
      onShowAnalysis={onShowAnalysis}
    />
  );
});

export default function ImageSimilarityDialog({ 
  open, 
  onClose, 
  sessionId, 
  selectedImages = [],
  selectedCount = 0
}) {
  // Backend API URL
  const backendUrl = getServerUrl();
  
  const [activeTab, setActiveTab] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [qualityReport, setQualityReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' });
  const [processedImages, setProcessedImages] = useState([]); // Track each image's status
  const [stats, setStats] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [clusterStats, setClusterStats] = useState(null); // Stats from clustering operation
  const [similarImages, setSimilarImages] = useState({});
  const [error, setError] = useState(null);
  const [stuckEmbedding, setStuckEmbedding] = useState(false); // 409 — previous run still in-progress in DB
  const [loading, setLoading] = useState(false);
  const [generateScope, setGenerateScope] = useState('selected'); // 'all' or 'selected'
  const [socket, setSocket] = useState(null);
  const [sessionImages, setSessionImages] = useState([]); // All images in session for thumbnail display
  
  // Clustering parameters
  const [clusterParams, setClusterParams] = useState({
    similarityThreshold: 0.85,
    minClusterSize: 1,
    maxClusters: 20
  });
  const [currentClusterParams, setCurrentClusterParams] = useState(null); // Parameters used for existing clusters
  const [expandedClusters, setExpandedClusters] = useState({}); // Track thumbnail count per cluster
  
  // Quality filters for clustering
  const [qualityFilters, setQualityFilters] = useState({
    excludeBlurry: false,
    requireAlbumWorthy: false,
    minQualityScore: 0,
    eventTypes: [],
    minPersonCount: null,
    maxPersonCount: null,
    requireSmile: false
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [forceRegenerate, setForceRegenerate] = useState(false); // Force regenerate embeddings
  const [imageQuality, setImageQuality] = useState('high'); // 'high' or 'low' for vision model
  const hasCheckedStatus = React.useRef(false); // Track if we've already checked generation status
  
  // Analysis details dialog state
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [selectedImageForAnalysis, setSelectedImageForAnalysis] = useState(null);
  
  // Check if parameters have been modified
  const paramsChanged = currentClusterParams && (
    clusterParams.similarityThreshold !== currentClusterParams.similarityThreshold ||
    clusterParams.minClusterSize !== currentClusterParams.minClusterSize ||
    clusterParams.maxClusters !== currentClusterParams.maxClusters
  );
  
  // Initialize Socket.IO connection when dialog opens
  useEffect(() => {
    if (!open) {
      return;
    }

    // Create socket when dialog opens - use same simple config as SceneTaggingDialog
    console.log('🔌 Initializing Socket.IO connection to:', backendUrl);
    
    const newSocket = io(backendUrl, {
      path: '/socket.io/',
      withCredentials: true
    });
    
    newSocket.on('connect', () => {
      console.log('✅ Socket.IO connected:', newSocket.id);
    });
    
    newSocket.on('connect_error', (error) => {
      console.error('❌ Socket.IO connection error:', error.message);
    });
    
    newSocket.on('disconnect', (reason) => {
      console.log('🔌 Socket.IO disconnected:', reason);
    });
    
    setSocket(newSocket);
    
    // Cleanup: close socket when dialog closes
    return () => {
      console.log('🔌 Closing Socket.IO connection (dialog closing)');
      newSocket.close();
      setSocket(null);
    };
  }, [open]); // Only depend on 'open' state
  
  // Track the actual selected count - update whenever selectedCount changes
  const actualSelectedCount = selectedCount > 0 ? selectedCount : selectedImages.length;

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Fetch stats when dialog opens or selection changes
  useEffect(() => {
    if (open && sessionId) {
      fetchStats();
      fetchSessionImages();
    }
  }, [open, sessionId, selectedCount]); // Track selectedCount

  // Fetch session images when Clusters tab is opened
  useEffect(() => {
    if (open && sessionId && activeTab === 2 && sessionImages.length === 0) {
      console.log('📸 Loading session images for cluster thumbnails...');
      fetchSessionImages();
    }
  }, [open, sessionId, activeTab, sessionImages.length]);

  const fetchSessionImages = async () => {
    try {
      // Fetch ALL images (no pagination limit) for cluster thumbnail matching
      const response = await axiosInstance.get(`/sessions/${sessionId}/images`, {
        params: {
          limit: 10000 // High limit to get all images
        }
      });
      console.log('🖼️ Session images received:', response.data);
      if (response.data && response.data.images) {
        console.log('🖼️ Total images loaded:', response.data.images.length);
        console.log('🖼️ First image structure:', response.data.images[0]);
        console.log('🖼️ Image ID fields:', {
          uniqueId: response.data.images[0]?.uniqueId,
          _id: response.data.images[0]?._id,
          imageId: response.data.images[0]?.imageId
        });
        setSessionImages(response.data.images);
      }
    } catch (err) {
      console.error('Error fetching session images:', err);
    }
  };

  const fetchQualityReport = async () => {
    setLoadingReport(true);
    try {
      const response = await fetch(`${backendUrl}/api/embeddings/session/${sessionId}/quality-report`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setQualityReport(data);
      }
    } catch (err) {
      console.error('Error fetching quality report:', err);
    } finally {
      setLoadingReport(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/embeddings/session/${sessionId}/stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Stats received:', data);
        setStats(data.stats || data); // Backend returns { stats: {...} }
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // WebSocket progress handler (memoized to prevent re-renders)
  const handleProgress = useCallback((data) => {
    // CRITICAL: Validate sessionId to prevent cross-session data leakage
    if (data.sessionId && data.sessionId !== sessionId) {
      console.warn('⚠️ Received progress for different session:', data.sessionId, 'expected:', sessionId);
      return;
    }
    
    let statusText;
    let statusIcon;
    if (data.status === 'skipped') {
      statusText = `⏭️ Skipped: ${data.filename || 'image'} (${data.current}/${data.total})`;
      statusIcon = '⏭️';
    } else if (data.status === 'success') {
      statusText = `✅ Completed: ${data.filename || 'image'} (${data.current}/${data.total})`;
      statusIcon = '✅';
    } else if (data.status === 'error') {
      statusText = `❌ Error: ${data.filename || 'image'} (${data.current}/${data.total})`;
      statusIcon = '❌';
    } else {
      statusText = data.filename 
        ? `Processing ${data.filename} (${data.current}/${data.total})`
        : `Processing image ${data.current} of ${data.total}...`;
      statusIcon = '🔄';
    }
    
    // Add estimated time remaining if available
    if (data.estimatedTimeRemainingFormatted) {
      statusText += ` - Est. ${data.estimatedTimeRemainingFormatted} remaining`;
    }
    
    console.log('📊 handleProgress data:', {
      current: data.current,
      total: data.total,
      status: data.status,
      lastProcessingTimeMs: data.lastProcessingTimeMs,
      avgProcessingTimeMs: data.avgProcessingTimeMs,
      actualEmbeddingCount: data.actualEmbeddingCount
    });
    
    setProgress({
      current: data.current,
      total: data.total,
      status: statusText,
      estimatedTimeRemainingMs: data.estimatedTimeRemainingMs,
      estimatedTimeRemaining: data.estimatedTimeRemainingFormatted,
      avgProcessingTimeMs: data.avgProcessingTimeMs,
      lastProcessingTimeMs: data.lastProcessingTimeMs
    });
    
    // Update stats in real-time if actual embedding count is provided
    // Use callback form to avoid dependency on stats
    if (data.actualEmbeddingCount !== undefined) {
      setStats(prev => ({
        ...prev,
        totalEmbeddings: data.actualEmbeddingCount,
        avgProcessingTimeMs: data.avgProcessingTimeMs,
        lastProcessingTimeMs: data.lastProcessingTimeMs
      }));
    }
    
    // Add to processed images list
    setProcessedImages(prev => [...prev, {
      filename: data.filename || 'unknown',
      status: data.status,
      icon: statusIcon,
      error: data.error
    }]);
  }, [sessionId]);

  // Create optimized image lookup map to avoid O(n) searches during render
  // This prevents performance issues when rendering many thumbnails
  const imageDataMap = useMemo(() => {
    const map = new Map();
    sessionImages.forEach(img => {
      // Store by all possible ID variants for maximum compatibility
      if (img.uniqueId) map.set(img.uniqueId, img);
      if (img.imageId) map.set(img.imageId, img);
      if (img._id) map.set(img._id, img);
    });
    return map;
  }, [sessionImages]);

  // Memoized handlers for cluster expansion to prevent re-renders
  const handleShowMore = useCallback((clusterId, currentCount, maxCount) => {
    setExpandedClusters(prev => ({
      ...prev,
      [clusterId]: Math.min((prev[clusterId] || 20) + 20, maxCount)
    }));
  }, []);

  const handleShowLess = useCallback((clusterId) => {
    setExpandedClusters(prev => {
      const updated = { ...prev };
      delete updated[clusterId];
      return updated;
    });
  }, []);

  // Handle showing analysis details for an image
  const handleShowAnalysis = useCallback((imageId) => {
    setSelectedImageForAnalysis(imageId);
    setAnalysisDialogOpen(true);
  }, []);

  const handleCloseAnalysisDialog = useCallback(() => {
    setAnalysisDialogOpen(false);
    setSelectedImageForAnalysis(null);
  }, []);

  // WebSocket complete handler (memoized)
  const handleComplete = useCallback(async (data) => {
    // CRITICAL: Validate sessionId to prevent cross-session data leakage
    if (data.sessionId && data.sessionId !== sessionId) {
      console.warn('⚠️ Received completion for different session:', data.sessionId, 'expected:', sessionId);
      return;
    }
    
    console.log('✅ Embedding generation complete via WebSocket!', data);
    setProgress(prev => ({
      ...prev,
      status: `Complete! Processed ${prev.current}/${prev.total} images`
    }));
    await fetchStats();
    setGenerating(false);
    
    // Clean up listeners
    if (socket) {
      socket.off('embeddingProgress', handleProgress);
      socket.off('embeddingComplete', handleComplete);
    }
  }, [socket, handleProgress, sessionId, fetchStats]);

  // Check if embedding generation is in progress (defined after handlers so it can reference them)
  const checkGenerationStatus = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/embeddings/generation-status/${sessionId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const status = await response.json();
        console.log('🔍 Generation status:', status);
        
        // Check if process is stale (backend crashed)
        if (status.isStale) {
          console.log('⚠️ Detected stale embedding process (backend may have crashed)');
          setError(
            'Embedding generation was interrupted (backend may have crashed). ' +
            'The process has been reset. You can restart generation if needed.'
          );
          setGenerating(false);
          return null;
        }
        
        if (status.inProgress) {
          console.log('⚠️ Embedding generation in progress, resuming display...');
          setGenerating(true);
          setProgress({
            current: status.current,
            total: status.total,
            status: `Processing ${status.current}/${status.total} images...`,
            estimatedTimeRemaining: status.estimatedTimeRemainingFormatted || null,
            avgProcessingTimeMs: status.avgProcessingTimeMs,
            lastProcessingTimeMs: status.lastProcessingTimeMs
          });
          
          // No polling needed - WebSocket will send real-time updates
          // Just register WebSocket listeners if not already registered
          if (socket && socket.connected) {
            socket.on('embeddingProgress', handleProgress);
            socket.on('embeddingComplete', handleComplete);
            console.log('📡 WebSocket listeners registered for in-progress generation');
          }
        }
      }
    } catch (err) {
      console.error('Error checking generation status:', err);
    }
    return null;
  }, [sessionId, socket, handleProgress, handleComplete]);

  // Check if embedding generation is in progress after socket and handlers are ready
  // Only check ONCE per dialog open to avoid repeated HTTP calls
  useEffect(() => {
    const isConnected = socket?.connected || false;
    
    if (open && sessionId && isConnected && !hasCheckedStatus.current) {
      console.log('🔍 Checking generation status (one-time check)...');
      hasCheckedStatus.current = true;
      checkGenerationStatus();
    }
    
    // Reset flag when dialog closes
    if (!open) {
      hasCheckedStatus.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId, socket?.connected]); // checkGenerationStatus is stable after initial render

  const generateEmbeddings = async () => {
    if (!socket || !socket.connected) {
      setError('WebSocket not connected. Please wait and try again.');
      return;
    }

    if (!socket.id) {
      setError('WebSocket ID not available. Please refresh and try again.');
      return;
    }

    // Check if generation is already in progress
    try {
      const statusResponse = await fetch(`${backendUrl}/api/embeddings/generation-status/${sessionId}`, {
        headers: getAuthHeaders()
      });
      if (statusResponse.ok) {
        const status = await statusResponse.json();
        
        // Check for stale process
        if (status.isStale) {
          setError(
            'A previous embedding generation was interrupted. The process has been reset. ' +
            'Please try again.'
          );
          return;
        }
        
        if (status.inProgress) {
          setStuckEmbedding(true);
          return;
        }
      }
    } catch (err) {
      console.error('Error checking generation status:', err);
    }

    setGenerating(true);
    setError(null);
    setProgress({ current: 0, total: 0, status: 'Starting...' });
    setProcessedImages([]); // Clear previous results

    try {
      const imageIds = generateScope === 'selected' 
        ? selectedImages.map(img => img.uniqueId || img._id)
        : undefined;

      console.log('Generating embeddings via WebSocket:', {
        scope: generateScope,
        imageIds,
        selectedCount: selectedImages.length,
        socketId: socket.id,
        socketConnected: socket.connected
      });

      // Register WebSocket event listeners
      socket.on('embeddingProgress', handleProgress);
      socket.on('embeddingComplete', handleComplete);

      // Prepare request body
      const requestBody = { 
        imageIds,
        socketId: socket.id,
        forceRegenerate,
        imageQuality
      };
      
      console.log('📤 Sending HTTP request with body:', requestBody);

      // Send HTTP request with socket ID
      const response = await fetch(`${backendUrl}/api/embeddings/generate/session/${sessionId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to start embedding generation');
      }

      const result = await response.json();
      console.log('HTTP response received:', result);
      
      // Handle cancellation result
      if (result.cancelled) {
        console.log('⚠️ Embedding generation was cancelled');
        setError('Embedding generation was cancelled');
        setGenerating(false);
        return;
      }
      
      // If HTTP completes but no WebSocket complete event yet, wait a bit then force complete
      setTimeout(() => {
        if (generating) {
          console.log('⚠️ Forcing completion - WebSocket event may have been missed');
          handleComplete({ result });
        }
      }, 1000);

    } catch (err) {
      console.error('❌ Error:', err);
      setError(err.message);
      setGenerating(false);
      
      // Clean up listeners on error
      if (socket) {
        socket.off('embeddingProgress');
        socket.off('embeddingComplete');
      }
    }
  };

  const cancelEmbeddings = async () => {
    if (!window.confirm('Are you sure you want to cancel the embedding generation?')) {
      return;
    }

    try {
      console.log('🛑 Cancelling embedding generation...');
      const response = await fetch(`${backendUrl}/api/embeddings/cancel/${sessionId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to cancel embedding generation');
      }

      const result = await response.json();
      console.log('✅ Cancellation result:', result);
      
      if (result.success) {
        setError(`Embedding generation cancelled. Processed ${result.processed} of ${result.total} images.`);
        setGenerating(false);
        await fetchStats();
      } else {
        setError(result.message || 'Failed to cancel generation');
      }
    } catch (err) {
      console.error('❌ Cancel error:', err);
      setError(`Failed to cancel: ${err.message}`);
    }
  };

  const deleteEmbeddings = async () => {
    if (!window.confirm('Are you sure you want to delete all embeddings? This will also clear cluster assignments. You will need to regenerate embeddings.')) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${backendUrl}/api/embeddings/session/${sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          imageIds: generateScope === 'selected' 
            ? selectedImages.map(img => img.uniqueId || img._id)
            : undefined
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete embeddings');
      }

      const result = await response.json();
      
      await fetchStats();
      setClusters([]);
      setClusterStats(null);
      
      setError(null);
      alert(`Successfully deleted ${result.deletedCount} embedding(s)`);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const findSimilarForImage = async (imageId) => {
    setLoading(true);
    console.log('🔍 Finding similar images for:', imageId);
    try {
      const response = await axiosInstance.get(`/embeddings/similar/${imageId}`, {
        params: { limit: 5 }
      });
      console.log('✅ Similar images response:', response.data);
      setSimilarImages(prev => ({
        ...prev,
        [imageId]: response.data.similarImages,
      }));
    } catch (err) {
      console.error('❌ Error finding similar images:', err);
      setError(`Failed to find similar images: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate/Re-generate clusters based on current parameters
   * 
   * WHAT HAPPENS DURING RE-CLUSTERING:
   * 1. Sends parameters to: POST /api/embeddings/cluster/session/:sessionId
   * 2. Backend calls: imageClusteringService.clusterSessionImages()
   * 3. Service builds similarity matrix using cosine similarity on embeddings
   * 4. Groups similar images into clusters based on threshold
   * 5. Updates ImageEmbedding collection for ALL images:
   *    - Sets clusterId field (e.g., "cluster_1", "cluster_2")
   *    - Sets clusterLabel field (e.g., "Same Location - Beach, Ocean")
   * 6. OLD cluster assignments are COMPLETELY REPLACED
   * 7. Images that don't meet criteria remain unclustered (clusterId = null)
   * 
   * NOTE: Re-clustering does NOT delete embeddings, only reassigns cluster IDs
   */
  const generateClusters = async () => {
    setLoading(true);
    setError(null);
    // Reset expanded state when regenerating clusters
    setExpandedClusters({});
    try {
      const response = await fetch(`${backendUrl}/api/embeddings/cluster/session/${sessionId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...clusterParams,
          filters: qualityFilters
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate clusters');
      }

      const result = await response.json();
      
      // Save the parameters that were used for this clustering
      if (result.parameters) {
        setCurrentClusterParams(result.parameters);
      } else {
        setCurrentClusterParams({ ...clusterParams });
      }
      
      // Save clustering stats (includes excludedByFilters count)
      if (result.stats) {
        setClusterStats(result.stats);
      }
      
      await fetchClusters();
      await fetchStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchClusters = async () => {
    console.log('🔄 Fetching clusters for session:', sessionId);
    try {
      const response = await fetch(`${backendUrl}/api/embeddings/clusters/session/${sessionId}`, {
        headers: getAuthHeaders()
      });
      console.log('📡 Clusters API response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('🎨 Clusters loaded:', data.clusters?.length || 0);
        if (data.clusters?.[0]) {
          console.log('🖼️ First cluster:', {
            id: data.clusters[0].id,
            size: data.clusters[0].size,
            imageCount: data.clusters[0].images?.length || 0,
            firstImageId: data.clusters[0].images?.[0]?.imageId
          });
        }
        setClusters(data.clusters);
        
        // If parameters are returned and we don't have current params set, use them
        if (data.parameters && !currentClusterParams) {
          setCurrentClusterParams(data.parameters);
          setClusterParams(data.parameters);
        }
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to fetch clusters:', response.status, errorData);
      }
    } catch (err) {
      console.error('❌ Error fetching clusters:', err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 2 && clusters.length === 0) {
      fetchClusters();
    }
  };

  const formatSimilarity = (score) => {
    return (score * 100).toFixed(1) + '%';
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { height: '80vh' } }}
      >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PsychologyIcon />
            <Typography variant="h6">Image Similarity Analysis</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<PsychologyIcon />} label="Generate Embeddings" />
          <Tab icon={<ImageSearchIcon />} label="Find Similar" disabled={!stats || stats.totalEmbeddings === 0} />
          <Tab icon={<CategoryIcon />} label="Clusters" disabled={!stats || stats.totalEmbeddings === 0} />
          <Tab icon={<AssessmentIcon />} label="Quality Report" disabled={!stats || stats.totalEmbeddings === 0} />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tab 1: Generate Embeddings */}
        <TabPanel value={activeTab} index={0}>
          <Box>
            {stats && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Session Statistics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Total Images
                      </Typography>
                      <Typography variant="h5">{stats.totalImages}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Embeddings Generated
                      </Typography>
                      <Typography variant="h5">{stats.totalEmbeddings}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Coverage
                      </Typography>
                      <Typography variant="h5">
                        {stats.totalImages > 0
                          ? ((stats.totalEmbeddings / stats.totalImages) * 100).toFixed(0)
                          : 0}
                        %
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Clustered
                      </Typography>
                      <Typography variant="h5">{stats.clusteredImages || 0}</Typography>
                    </Grid>
                    {stats.avgProcessingTimeMs && stats.avgProcessingTimeMs > 0 && (
                      <>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Avg Time/Image
                          </Typography>
                          <Typography variant="h6">
                            {(stats.avgProcessingTimeMs / 1000).toFixed(1)}s
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Est. Time for All
                          </Typography>
                          <Typography variant="h6">
                            {(() => {
                              const remainingImages = stats.totalImages - stats.totalEmbeddings;
                              const totalMs = remainingImages * stats.avgProcessingTimeMs;
                              const hours = Math.floor(totalMs / (1000 * 60 * 60));
                              const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
                              if (hours > 0) return `${hours}h ${minutes}m`;
                              if (minutes > 0) return `${minutes}m`;
                              return `${Math.floor(totalMs / 1000)}s`;
                            })()}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            <Typography variant="body1" paragraph>
              Generate AI-powered embeddings for images in this session. This enables similarity
              search and automatic clustering based on visual content.
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">Generate embeddings for:</FormLabel>
              <RadioGroup
                value={generateScope}
                onChange={(e) => setGenerateScope(e.target.value)}
              >
                <FormControlLabel
                  value="selected"
                  control={<Radio />}
                  label={`Selected images only (${actualSelectedCount} images)`}
                  disabled={actualSelectedCount === 0 || generating}
                />
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={`All images in session (${stats?.totalImages || 0} images)`}
                  disabled={generating}
                />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">Image Quality:</FormLabel>
              <RadioGroup
                row
                value={imageQuality}
                onChange={(e) => setImageQuality(e.target.value)}
              >
                <FormControlLabel
                  value="high"
                  control={<Radio />}
                  label="High (slower, more accurate)"
                  disabled={generating}
                />
                <FormControlLabel
                  value="low"
                  control={<Radio />}
                  label="Low (3x faster, 66% cheaper)"
                  disabled={generating}
                />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                {imageQuality === 'high' 
                  ? 'High resolution (2048x2048) - best for blur detection, face analysis, quality scoring. ~$0.00765/image, 3-8 sec/image' 
                  : 'Low resolution (512x512) - sufficient for similarity, colors, scenes. ~$0.00255/image, 1-3 sec/image'}
              </Typography>
            </FormControl>

            {actualSelectedCount === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Select images in the gallery first to generate embeddings for specific images, or choose "All images" to process the entire session.
              </Alert>
            )}

            {(generating || progress.total > 0) && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {progress.status}
                  </Typography>
                  {progress.estimatedTimeRemaining && (
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                      ⏱️ {progress.estimatedTimeRemaining} left
                    </Typography>
                  )}
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {progress.current} / {progress.total}
                  </Typography>
                  {(progress.avgProcessingTimeMs || progress.lastProcessingTimeMs) && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {progress.lastProcessingTimeMs > 0 && (
                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 'medium' }}>
                          Last: {(progress.lastProcessingTimeMs / 1000).toFixed(1)}s
                        </Typography>
                      )}
                      {progress.avgProcessingTimeMs && (
                        <Typography variant="caption" color="text.secondary">
                          Avg: {(progress.avgProcessingTimeMs / 1000).toFixed(1)}s
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
                
                {/* Status list for each processed image */}
                {processedImages.length > 0 && (
                  <Box sx={{ mt: 2, maxHeight: 200, overflowY: 'auto', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                      Processing Status ({processedImages.length} images):
                    </Typography>
                    {processedImages.map((img, index) => (
                      <Typography 
                        key={index} 
                        variant="caption" 
                        display="block" 
                        sx={{ 
                          py: 0.5,
                          fontFamily: 'monospace',
                          color: img.status === 'error' ? 'error.main' : 
                                 img.status === 'success' ? 'success.main' : 
                                 'text.secondary'
                        }}
                      >
                        {img.icon} {img.filename} {img.error ? `- ${img.error}` : ''}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
            
            {/* Debug: Show progress even when not generating */}
            {!generating && progress.total > 0 && processedImages.length === 0 && (
              <Box sx={{ mb: 2, p: 1, bgcolor: 'warning.light' }}>
                <Typography variant="caption" color="text.secondary">
                  DEBUG: generating={String(generating)}, status={progress.status}, {progress.current}/{progress.total}
                </Typography>
              </Box>
            )}

            {stats && stats.totalEmbeddings > 0 && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Regenerate Options
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={forceRegenerate}
                      onChange={(e) => setForceRegenerate(e.target.checked)}
                      disabled={generating}
                    />
                  }
                  label="Force regenerate existing embeddings (updates quality analysis)"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Check this to regenerate embeddings with new quality analysis fields (person count, smile detection, blur detection, event type, etc.)
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={deleteEmbeddings}
                  disabled={loading || generating}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Delete {generateScope === 'selected' ? 'Selected' : 'All'} Embeddings
                </Button>
              </Box>
            )}

            {stuckEmbedding && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="warning"
                    size="small"
                    variant="outlined"
                    onClick={async () => {
                      try {
                        const resp = await fetch(`${backendUrl}/api/embeddings/force-reset/${sessionId}`, {
                          method: 'POST',
                          headers: getAuthHeaders(),
                        });
                        if (!resp.ok) throw new Error('Force-reset failed');
                        setStuckEmbedding(false);
                        generateEmbeddings();
                      } catch (err) {
                        setError('Failed to clear stuck operation: ' + err.message);
                      }
                    }}
                  >
                    Force Reset &amp; Retry
                  </Button>
                }
              >
                A previous embedding run is still marked as <strong>in progress</strong> in the
                database (the backend may have restarted mid-run). Click{' '}
                <strong>Force Reset &amp; Retry</strong> to clear it and start a new run.
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={generating ? cancelEmbeddings : generateEmbeddings}
              disabled={
                generating 
                  ? false // Cancel always enabled during generation
                  : (generateScope === 'selected' && actualSelectedCount === 0) || !socket?.connected
              }
              fullWidth
              size="large"
              startIcon={generating ? <CircularProgress size={20} /> : <PsychologyIcon />}
              color={generating ? 'error' : 'primary'}
            >
              {generating 
                ? 'Cancel Generation' 
                : `Generate Embeddings for ${generateScope === 'selected' ? `${actualSelectedCount} Selected` : 'All'} Images`}
            </Button>

            <Typography variant="caption" color={socket?.connected ? 'success.main' : 'warning.main'} display="block" sx={{ mt: 0.5 }}>
              WebSocket: {socket?.connected ? `✅ Connected (${socket.id?.substring(0, 8)}...)` : '⚠️ Connecting...'}
            </Typography>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Estimated Cost: ~${((generateScope === 'selected' ? actualSelectedCount : stats?.totalImages || 0) * (imageQuality === 'high' ? 0.00765 : 0.00255)).toFixed(4)} 
              {' '}({generateScope === 'selected' ? actualSelectedCount : stats?.totalImages || 0} images × ${imageQuality === 'high' ? '$0.00765' : '$0.00255'} per image)
            </Typography>
          </Box>
        </TabPanel>

        {/* Tab 2: Find Similar */}
        <TabPanel value={activeTab} index={1}>
          {useMemo(() => (
            <>
              <Typography variant="body1" paragraph>
                Select an image to find visually similar images in this session.
              </Typography>

              {actualSelectedCount > 0 ? (
                <Grid container spacing={2}>
                  {selectedImages.map((image) => {
                    const imageId = image.uniqueId || image._id;
                    return (
                <Grid item xs={12} sm={6} md={4} key={imageId}>
                  <Card>
                    <Box sx={{ position: 'relative', width: '100%', height: 200 }}>
                      <AuthenticatedImage
                        imageId={imageId}
                        type="thumbnail"
                        alt={image.name || 'Selected image'}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                    <CardContent>
                      <Typography variant="caption" display="block" gutterBottom>
                        {image.name}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => findSimilarForImage(imageId)}
                        disabled={loading}
                      >
                        Find Similar
                      </Button>

                      {similarImages[imageId] && similarImages[imageId].length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Similar Images:
                          </Typography>
                          <Grid container spacing={1}>
                            {similarImages[imageId].map((similar, idx) => (
                              <Grid item xs={6} key={similar.imageId || idx}>
                                <Box>
                                  <AuthenticatedImage
                                    imageId={similar.imageId}
                                    type="thumbnail"
                                    alt="Similar image"
                                    sx={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 1 }}
                                  />
                                  <Typography variant="caption" display="block" align="center" sx={{ mt: 0.5 }}>
                                    {(similar.similarity * 100).toFixed(1)}%
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
              })}
            </Grid>
          ) : (
            <Alert severity="info">
              Select images from the gallery to find similar images.
            </Alert>
          )}
            </>
          ), [actualSelectedCount, selectedImages, similarImages, loading])}
        </TabPanel>

        {/* Tab 3: Clusters */}
        <TabPanel value={activeTab} index={2}>
          <>
            <Box display="flex" flexDirection="column" gap={2} sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">
                  Automatic grouping of visually similar images.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={generateClusters}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                >
                  {clusters.length > 0 ? 'Re-cluster' : 'Generate Clusters'}
                </Button>
              </Box>

                  {/* Clustering Parameters */}
                  <Card variant="outlined" sx={{ bgcolor: paramsChanged ? 'warning.lighter' : 'background.paper' }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">
                          Clustering Parameters
                          {currentClusterParams && !paramsChanged && (
                            <Chip 
                              label="Current" 
                              size="small" 
                              color="success" 
                              sx={{ ml: 1 }} 
                            />
                          )}
                          {paramsChanged && (
                            <Chip 
                              label="Modified - Click Re-cluster to apply" 
                              size="small" 
                              color="warning" 
                              sx={{ ml: 1 }} 
                            />
                          )}
                        </Typography>
                        {paramsChanged && (
                          <Button 
                            size="small" 
                            onClick={() => setClusterParams({ ...currentClusterParams })}
                          >
                            Reset
                          </Button>
                        )}
                      </Box>
                      
                      {currentClusterParams && (
                        <Alert severity="info" sx={{ mb: 2, py: 0 }}>
                          <Typography variant="caption">
                            <strong>Current clusters:</strong> Similarity {(currentClusterParams.similarityThreshold * 100).toFixed(0)}%, 
                            Min size {currentClusterParams.minClusterSize}, 
                            Max {currentClusterParams.maxClusters} clusters
                          </Typography>
                        </Alert>
                      )}
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" gutterBottom>
                            Similarity Threshold: {(clusterParams.similarityThreshold * 100).toFixed(1)}%
                          </Typography>
                          <Slider
                            value={clusterParams.similarityThreshold}
                            onChange={(e, value) => setClusterParams(prev => ({ ...prev, similarityThreshold: value }))}
                            min={0.5}
                            max={0.99}
                            step={0.01}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${(value * 100).toFixed(1)}%`}
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            Higher = stricter grouping
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" gutterBottom>
                            Min Cluster Size: {clusterParams.minClusterSize}
                          </Typography>
                          <Slider
                            value={clusterParams.minClusterSize}
                            onChange={(e, value) => setClusterParams(prev => ({ ...prev, minClusterSize: value }))}
                            min={1}
                            max={10}
                            step={1}
                            marks
                            valueLabelDisplay="auto"
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            Minimum images per cluster
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" gutterBottom>
                            Max Clusters: {clusterParams.maxClusters}
                          </Typography>
                          <Slider
                            value={clusterParams.maxClusters}
                            onChange={(e, value) => setClusterParams(prev => ({ ...prev, maxClusters: value }))}
                            min={5}
                            max={stats?.totalImages || 50}
                            step={5}
                            marks={false}
                            valueLabelDisplay="auto"
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            Maximum number of clusters (max: {stats?.totalImages || 50})
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Advanced Quality Filters */}
                  <Accordion expanded={showAdvancedFilters} onChange={() => setShowAdvancedFilters(!showAdvancedFilters)}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <FilterListIcon />
                        <Typography variant="subtitle2">
                          Advanced Quality Filters
                          {(qualityFilters.excludeBlurry || qualityFilters.requireAlbumWorthy || qualityFilters.minQualityScore > 0 || qualityFilters.eventTypes.length > 0) && (
                            <Chip label="Active" size="small" color="primary" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Filter out unwanted images before clustering (outliers, screenshots, blurry images, etc.)
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={qualityFilters.excludeBlurry}
                                onChange={(e) => setQualityFilters(prev => ({ ...prev, excludeBlurry: e.target.checked }))}
                              />
                            }
                            label="Exclude blurry images"
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={qualityFilters.requireAlbumWorthy}
                                onChange={(e) => setQualityFilters(prev => ({ ...prev, requireAlbumWorthy: e.target.checked }))}
                              />
                            }
                            label="Album-worthy photos only"
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={qualityFilters.requireSmile}
                                onChange={(e) => setQualityFilters(prev => ({ ...prev, requireSmile: e.target.checked }))}
                              />
                            }
                            label="Require smiling faces"
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" gutterBottom>
                            Minimum Quality Score: {qualityFilters.minQualityScore}/10
                          </Typography>
                          <Slider
                            value={qualityFilters.minQualityScore}
                            onChange={(e, value) => setQualityFilters(prev => ({ ...prev, minQualityScore: value }))}
                            min={0}
                            max={10}
                            step={1}
                            marks
                            valueLabelDisplay="auto"
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Min Persons"
                            type="number"
                            size="small"
                            fullWidth
                            value={qualityFilters.minPersonCount || ''}
                            onChange={(e) => setQualityFilters(prev => ({ ...prev, minPersonCount: e.target.value ? parseInt(e.target.value) : null }))}
                            InputProps={{ inputProps: { min: 0 } }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Max Persons"
                            type="number"
                            size="small"
                            fullWidth
                            value={qualityFilters.maxPersonCount || ''}
                            onChange={(e) => setQualityFilters(prev => ({ ...prev, maxPersonCount: e.target.value ? parseInt(e.target.value) : null }))}
                            InputProps={{ inputProps: { min: 0 } }}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Event Types to Include</InputLabel>
                            <Select
                              multiple
                              value={qualityFilters.eventTypes}
                              onChange={(e) => setQualityFilters(prev => ({ ...prev, eventTypes: e.target.value }))}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map((value) => (
                                    <Chip key={value} label={value} size="small" />
                                  ))}
                                </Box>
                              )}
                            >
                              <MenuItem value="wedding">Wedding</MenuItem>
                              <MenuItem value="travel">Travel</MenuItem>
                              <MenuItem value="sport">Sport</MenuItem>
                              <MenuItem value="party">Party</MenuItem>
                              <MenuItem value="family">Family</MenuItem>
                              <MenuItem value="professional">Professional</MenuItem>
                              <MenuItem value="nature">Nature</MenuItem>
                              <MenuItem value="food">Food</MenuItem>
                              <MenuItem value="architecture">Architecture</MenuItem>
                              <MenuItem value="other">Other</MenuItem>
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Leave empty to include all event types
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Button
                            size="small"
                            onClick={() => setQualityFilters({
                              excludeBlurry: false,
                              requireAlbumWorthy: false,
                              minQualityScore: 0,
                              eventTypes: [],
                              minPersonCount: null,
                              maxPersonCount: null,
                              requireSmile: false
                            })}
                          >
                            Clear All Filters
                          </Button>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                {clusters.length > 0 ? (
                  <>
                    {/* Cluster Statistics */}
                    <Card variant="outlined" sx={{ mb: 2, bgcolor: 'primary.lighter' }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Cluster Statistics
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={2}>
                            <Typography variant="body2" color="text.secondary">
                              Total Clusters
                            </Typography>
                            <Typography variant="h6">{clusters.length}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Typography variant="body2" color="text.secondary">
                              Images Clustered
                            </Typography>
                            <Typography variant="h6">
                              {clusters.reduce((sum, c) => sum + (c.imageCount || 0), 0)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Typography variant="body2" color="text.secondary">
                              Unclustered
                            </Typography>
                            <Typography variant="h6">
                              {clusterStats?.unclustered ?? ((stats?.totalImages || 0) - clusters.reduce((sum, c) => sum + (c.imageCount || 0), 0))}
                            </Typography>
                          </Grid>
                          {clusterStats?.excludedByFilters > 0 && (
                            <Grid item xs={6} sm={2}>
                              <Typography variant="body2" color="text.secondary">
                                Filtered Out
                              </Typography>
                              <Typography variant="h6" color="warning.main">
                                {clusterStats.excludedByFilters}
                              </Typography>
                            </Grid>
                          )}
                          <Grid item xs={6} sm={clusterStats?.excludedByFilters > 0 ? 2 : 2}>
                            <Typography variant="body2" color="text.secondary">
                              Avg/Cluster
                            </Typography>
                            <Typography variant="h6">
                              {(clusters.reduce((sum, c) => sum + (c.imageCount || 0), 0) / clusters.length).toFixed(1)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={clusterStats?.excludedByFilters > 0 ? 2 : 2}>
                            <Typography variant="body2" color="text.secondary">
                              Largest
                            </Typography>
                            <Typography variant="h6">
                              {Math.max(...clusters.map(c => c.imageCount || 0))}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={clusterStats?.excludedByFilters > 0 ? 2 : 2}>
                            <Typography variant="body2" color="text.secondary">
                              Smallest
                            </Typography>
                            <Typography variant="h6">
                              {Math.min(...clusters.map(c => c.imageCount || 0))}
                            </Typography>
                          </Grid>
                        </Grid>
                        {clusterStats?.excludedByFilters > 0 && (
                          <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
                            <Typography variant="caption">
                              {clusterStats.excludedByFilters} image{clusterStats.excludedByFilters !== 1 ? 's' : ''} excluded by quality filters
                            </Typography>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                    
                    <Grid container spacing={2}>
                      {clusters.map((cluster, clusterIndex) => (
                        <ClusterCardWrapper
                          key={cluster.clusterId}
                          cluster={cluster}
                          clusterIndex={clusterIndex}
                          imageDataMap={imageDataMap}
                          expandedClusters={expandedClusters}
                          handleShowMore={handleShowMore}
                          handleShowLess={handleShowLess}
                          onShowAnalysis={handleShowAnalysis}
                        />
                      ))}
                    </Grid>
                  </>
                ) : (
                  <Alert severity="info">
                    No clusters generated yet. Click "Generate Clusters" to automatically group similar images.
                  </Alert>
                )}
              </Box>
          </>
        </TabPanel>

        {/* Tab 4: Quality Report */}
        <TabPanel value={activeTab} index={3}>
          <QualityReportTab
            sessionId={sessionId}
            qualityReport={qualityReport}
            loading={loadingReport}
            onLoad={fetchQualityReport}
            getAuthHeaders={getAuthHeaders}
          />
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
      
    {/* Image Analysis Details Dialog */}
    <ImageAnalysisDetailsDialog
      open={analysisDialogOpen}
      onClose={handleCloseAnalysisDialog}
      imageId={selectedImageForAnalysis}
    />
    </>
  );
}
